import { useMemo, useState } from 'react';
import { Badge, Form } from 'react-bootstrap';

const TIPOS_FILTRO = [
    { value: 'todos', label: 'Todos' },
    { value: 'Aluno', label: 'Aluno' },
    { value: 'Gestor', label: 'Gestor' },
    { value: 'RH', label: 'RH' },
];

function TabelaSelecaoUsuariosPratica({
    usuarios = [],
    praticasRegistradas = {},
    selectedIds = [],
    onSelectionChange,
    loading = false,
}) {
    const [filtroTipo, setFiltroTipo] = useState('todos');
    const [busca, setBusca] = useState('');

    const usuariosEnriquecidos = useMemo(() => {
        return usuarios.map((usuario) => {
            const pratica = praticasRegistradas[usuario.id];
            const praticaAtiva = pratica?.ativa === true || pratica?.ativa === 1;
            return {
                ...usuario,
                nivel: usuario.nivel || 'Aluno',
                praticaAtiva,
                elegivel: !praticaAtiva,
            };
        });
    }, [usuarios, praticasRegistradas]);

    const usuariosFiltrados = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        return usuariosEnriquecidos.filter((usuario) => {
            if (filtroTipo !== 'todos' && usuario.nivel !== filtroTipo) {
                return false;
            }
            if (!termo) {
                return true;
            }
            return (
                usuario.nome_completo?.toLowerCase().includes(termo)
                || usuario.codigo?.toString().toLowerCase().includes(termo)
                || usuario.email?.toLowerCase().includes(termo)
            );
        });
    }, [usuariosEnriquecidos, filtroTipo, busca]);

    const elegiveisFiltrados = useMemo(
        () => usuariosFiltrados.filter((u) => u.elegivel),
        [usuariosFiltrados]
    );

    const todosElegiveisSelecionados = elegiveisFiltrados.length > 0
        && elegiveisFiltrados.every((u) => selectedIds.includes(u.id));

    const handleToggleTodos = () => {
        if (todosElegiveisSelecionados) {
            const idsFiltrados = new Set(elegiveisFiltrados.map((u) => u.id));
            onSelectionChange(selectedIds.filter((id) => !idsFiltrados.has(id)));
            return;
        }

        const novosIds = new Set([...selectedIds, ...elegiveisFiltrados.map((u) => u.id)]);
        onSelectionChange(Array.from(novosIds));
    };

    const handleToggleUsuario = (id, elegivel) => {
        if (!elegivel) {
            return;
        }

        if (selectedIds.includes(id)) {
            onSelectionChange(selectedIds.filter((item) => item !== id));
            return;
        }

        onSelectionChange([...selectedIds, id]);
    };

    const badgeNivel = (nivel) => {
        const map = {
            Aluno: 'primary',
            Gestor: 'warning',
            RH: 'info',
        };
        return <Badge bg={map[nivel] || 'secondary'}>{nivel}</Badge>;
    };

    return (
        <div className="card border-0 shadow-sm">
            <div className="card-header bg-primary">
                <h6 className="mb-0 text-white fw-bold">
                    <i className="bi bi-person-check me-2"></i>
                    Selecionar Participantes
                </h6>
                <small className="text-white">
                    {selectedIds.length} selecionado(s) · usuários com prática ativa não podem ser selecionados
                </small>
            </div>
            <div className="card-body">
                <div className="row g-2 mb-3">
                    <div className="col-md-8">
                        <input
                            type="search"
                            className="form-control"
                            placeholder="Buscar por nome, matrícula ou e-mail..."
                            value={busca}
                            onChange={(e) => setBusca(e.target.value)}
                        />
                    </div>
                    <div className="col-md-4">
                        <Form.Select
                            value={filtroTipo}
                            onChange={(e) => setFiltroTipo(e.target.value)}
                        >
                            {TIPOS_FILTRO.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </Form.Select>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                        <table className="table table-sm table-hover align-middle mb-0">
                            <thead className="table-light sticky-top">
                                <tr>
                                    <th style={{ width: '40px' }}>
                                        <Form.Check
                                            type="checkbox"
                                            checked={todosElegiveisSelecionados}
                                            disabled={elegiveisFiltrados.length === 0}
                                            onChange={handleToggleTodos}
                                            aria-label="Selecionar todos elegíveis"
                                        />
                                    </th>
                                    <th>Nome</th>
                                    <th>Matrícula</th>
                                    <th>Tipo</th>
                                    <th>Prática</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usuariosFiltrados.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center text-muted py-3">
                                            Nenhum usuário encontrado
                                        </td>
                                    </tr>
                                ) : (
                                    usuariosFiltrados.map((usuario) => (
                                        <tr key={usuario.id} className={!usuario.elegivel ? 'text-muted' : ''}>
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedIds.includes(usuario.id)}
                                                    disabled={!usuario.elegivel}
                                                    onChange={() => handleToggleUsuario(usuario.id, usuario.elegivel)}
                                                    aria-label={`Selecionar ${usuario.nome_completo}`}
                                                />
                                            </td>
                                            <td>{usuario.nome_completo}</td>
                                            <td>{usuario.codigo}</td>
                                            <td>{badgeNivel(usuario.nivel)}</td>
                                            <td>
                                                {usuario.praticaAtiva ? (
                                                    <Badge bg="secondary">Ativa</Badge>
                                                ) : (
                                                    <Badge bg="success">Disponível</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TabelaSelecaoUsuariosPratica;
